import React from 'react';
import { Tabs } from 'flowbite-react';
import Image from 'next/image';
import Link from 'next/link';

const ScmSoftwareDevelopment = () => {
    return (
        <div className="section-py bg-primary dark:text-light">
            <div className="container xl:container mx-auto">
                <div className="row grid lg:grid-cols-12 mb-11">
                    <div className="lg:col-span-12">
                        <h2 className="text-white">Steps We Follow for SCM Software Development</h2>
                    </div>
                </div>
                <div className="row grid lg:grid-cols-12 scm-software-dev-tab mb-12">
                    <div className="lg:col-span-12 grid">
                        <Tabs.Group
                            className="scm-software-dev-tab-items bg-white rounded-10"
                            aria-label="underline tabs"
                            style="underline"
                            theme={{
                                tablist: {
                                    base: 'flex w-full justify-between border-b rounded-b-none !rounded-bl-none !rounded-br-none',
                                    tabitem: {
                                        base: `
                                            flex-1 text-center 
                                            first:rounded-tl-[10px] 
                                            last:rounded-tr-[10px]
                                            first:!rounded-bl-none 
                                            last:!rounded-br-none
                                            `,
                                    },
                                },
                            }}
                        >
                            <Tabs.Item
                                active
                                title={
                                    <div className="flex items-center justify-center gap-2 relative res-display">
                                        Scope Analysis Process
                                        <span className="absolute right-[-12px] top-1/2 -translate-y-1/2">
                                            <Image
                                                src="/img/icons/arrow-right-vector.svg"
                                                className="img-fluid"
                                                alt="arrow-right"
                                                title="arrow-right"
                                                height={30}
                                                width={30}
                                            />
                                        </span>
                                    </div>
                                }
                            >
                                <div className="lg:col-span-10 py-6 px-2 lg:px-9 md-px-2">
                                    <p className="font-semibold text-primary_one mb-7">
                                        Before we write a single line of code, we align with your business goals, technical
                                        realities, and user needs.
                                    </p>
                                    <div className="flex lg:flex-nowrap md:flex-wrap flex-wrap gap-7 lg:gap-0 md:gap-0">
                                        <div className="pr-0 lg:pr-7 md:pr-3">
                                            <div className="voice-generator-box mb-2">
                                                <Image
                                                    src="/img/moweb-supply-chain-icons/ic-requirement-gathering.svg"
                                                    width={36}
                                                    height={36}
                                                    alt="requirement-gathering"
                                                />
                                            </div>
                                            <p className="font-bold text-primary_one fs-lg mb-2">Requirement Gathering</p>
                                            <p className="fs-md mb-0">
                                                We work with your team to define core needs, pain points, and must-have features
                                                across the supply chain lifecycle.
                                            </p>
                                        </div>
                                        <div className="border-r-0 border-l-0 lg:border-r lg:border-l md:border-r md:border-l pl-0 lg:pl-7 md:pl-3 pr-0 lg:pr-7 md:pr-3">
                                            <div className="voice-generator-box mb-2">
                                                <Image
                                                    src="/img/moweb-supply-chain-icons/ic-detailed-scope.svg"
                                                    width={36}
                                                    height={36}
                                                    alt="detailed-scope"
                                                />
                                            </div>
                                            <p className="font-bold text-primary_one fs-lg mb-2">Detailed Scope Documentation</p>
                                            <p className="fs-md mb-0">
                                                Every feature is mapped, every workflow documented, ensuring total clarity before
                                                development begins.
                                            </p>
                                        </div>
                                        <div className="border-r-0 lg:border-r md:border-r pl-0 lg:pl-7 md:pl-3 pr-0 lg:pr-7 md:pr-3">
                                            <div className="voice-generator-box mb-2">
                                                <Image
                                                    src="/img/moweb-supply-chain-icons/ic-wireframes.svg"
                                                    width={36}
                                                    height={36}
                                                    alt="wireframes"
                                                />
                                            </div>
                                            <p className="font-bold text-primary_one fs-lg mb-2">Wireframes</p>
                                            <p className="fs-md mb-0">
                                                Get a visual blueprint of your future SCM platform, designed for usability,
                                                efficiency, and seamless flow.
                                            </p>
                                        </div>
                                        <div className="pl-0 lg:pl-7 md:pl-3">
                                            <div className="voice-generator-box mb-2">
                                                <Image
                                                    src="/img/moweb-supply-chain-icons/ic-technical-feasibility.svg"
                                                    width={36}
                                                    height={36}
                                                    alt="technical-feasibility"
                                                />
                                            </div>
                                            <p className="font-bold text-primary_one fs-lg mb-2">Technical Feasibility</p>
                                            <p className="fs-md mb-0">
                                                We validate tech stacks, integrations, and infrastructure needs to make sure your
                                                platform is scalable and future-proof.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Tabs.Item>
                            <Tabs.Item
                                title={
                                    <div className="flex items-center justify-center gap-2 relative res-display">
                                        Sprint Planning & Execution
                                        <span className="absolute right-[-12px] top-1/2 -translate-y-1/2">
                                            <Image
                                                src="/img/icons/arrow-right-vector.svg"
                                                className="img-fluid"
                                                alt="arrow-right"
                                                title="arrow-right"
                                                height={30}
                                                width={36}
                                            />
                                        </span>
                                    </div>
                                }
                            >
                                <div className="lg:col-span-10 py-6 px-2 lg:px-9 md-px-2">
                                    <p className="font-semibold text-primary_one mb-7">
                                        We move fast, but always with precision. Agile execution ensures steady progress, constant
                                        collaboration, and quick pivots.
                                    </p>
                                    <div className="flex lg:flex-nowrap md:flex-wrap flex-wrap gap-7 lg:gap-0 md:gap-0">
                                        <div className="pr-0 lg:pr-7 md:pr-3">
                                            <div className="voice-generator-box mb-2">
                                                <Image
                                                    src="/img/moweb-supply-chain-icons/ic-sprint-planning.svg"
                                                    width={36}
                                                    height={36}
                                                    alt="sprint-planning"
                                                />
                                            </div>
                                            <p className="font-bold text-primary_one fs-lg mb-2">Sprint Planning</p>
                                            <p className="fs-md mb-0">
                                                We break down your scope into manageable milestones and define clear success
                                                criteria for each sprint.
                                            </p>
                                        </div>
                                        <div className="border-r-0 border-l-0 lg:border-r lg:border-l md:border-r md:border-l pl-0 lg:pl-7 md:pl-3 pr-0 lg:pr-7 md:pr-3">
                                            <div className="voice-generator-box mb-2">
                                                <Image
                                                    src="/img/moweb-supply-chain-icons/ic-daily-standup.svg"
                                                    width={36}
                                                    height={36}
                                                    alt="daily-standup"
                                                />
                                            </div>
                                            <p className="font-bold text-primary_one fs-lg mb-2">Daily Standup Meetings</p>
                                            <p className="fs-md mb-0">
                                                Stay in the loop with daily progress check-ins, blockers, and key updates from the
                                                team.
                                            </p>
                                        </div>
                                        <div className="border-r-0 lg:border-r md:border-r pl-0 lg:pl-7 md:pl-3 pr-0 lg:pr-7 md:pr-3">
                                            <div className="voice-generator-box mb-2">
                                                <Image
                                                    src="/img/moweb-supply-chain-icons/ic-designing-&-development.svg"
                                                    width={36}
                                                    height={36}
                                                    alt="designing-&-development"
                                                />
                                            </div>
                                            <p className="font-bold text-primary_one fs-lg mb-2">Designing & Development</p>
                                            <p className="fs-md mb-0">
                                                Our team builds, tests, and refines every module—from inventory tracking to
                                                logistics analytics.
                                            </p>
                                        </div>
                                        <div className="pl-0 lg:pl-7 md:pl-3">
                                            <div className="voice-generator-box mb-2">
                                                <Image
                                                    src="/img/moweb-supply-chain-icons/ic-testing-&-stage.svg"
                                                    width={36}
                                                    height={36}
                                                    alt="testing-&-stage"
                                                />
                                            </div>
                                            <p className="font-bold text-primary_one fs-lg mb-2">
                                                Testing & Stage/Dev Deployment
                                            </p>
                                            <p className="fs-md mb-0">
                                                Features are rigorously tested in sandbox environments to ensure performance,
                                                security, and compliance.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Tabs.Item>
                            <Tabs.Item
                                title={
                                    <div className="flex items-center justify-center gap-2 relative res-display">
                                        UAT & Release
                                        <span className="absolute right-[-12px] top-1/2 -translate-y-1/2">
                                            <Image
                                                src="/img/icons/arrow-right-vector.svg"
                                                className="img-fluid"
                                                alt="arrow-right"
                                                title="arrow-right"
                                                height={30}
                                                width={36}
                                            />
                                        </span>
                                    </div>
                                }
                            >
                                <div className="lg:col-span-10 py-6 px-2 lg:px-9 md-px-2">
                                    <p className="font-semibold text-primary_one mb-7">
                                        You review, we refine. Once it&apos;s perfect, we go live.
                                    </p>
                                    <div className="flex lg:flex-nowrap md:flex-wrap flex-wrap gap-7 lg:gap-0 md:gap-0">
                                        <div className="pr-0 lg:pr-7 md:pr-3">
                                            <div className="voice-generator-box mb-2">
                                                <Image
                                                    src="/img/moweb-supply-chain-icons/ic-uat-feedback.svg"
                                                    width={36}
                                                    height={36}
                                                    alt="uat-feedback"
                                                />
                                            </div>
                                            <p className="font-bold text-primary_one fs-lg mb-2">UAT Feedback & Bug Resolution</p>
                                            <p className="fs-md mb-0">
                                                Your team validates every feature. We fix anything that&apos;s not up to standard,
                                                fast.
                                            </p>
                                        </div>
                                        <div className="border-r-0 border-l-0 lg:border-r lg:border-l md:border-r md:border-l pl-0 lg:pl-7 md:pl-3 pr-0 lg:pr-7 md:pr-3">
                                            <div className="voice-generator-box mb-2">
                                                <Image
                                                    src="/img/moweb-supply-chain-icons/ic-deliver-well-crafted.svg"
                                                    width={36}
                                                    height={36}
                                                    alt="deliver-well-crafted"
                                                />
                                            </div>
                                            <p className="font-bold text-primary_one fs-lg mb-2">Deliver Well-Crafted Software</p>
                                            <p className="fs-md mb-0">
                                                Once approved, we deliver fully functional, performance-optimized software
                                                that&apos;s ready to scale.
                                            </p>
                                        </div>
                                        <div className="pl-0 lg:pl-7 md:pl-3">
                                            <div className="voice-generator-box mb-2">
                                                <Image
                                                    src="/img/moweb-supply-chain-icons/ic-deploy-all-assets.svg"
                                                    width={36}
                                                    height={36}
                                                    alt="deploy-all-assets"
                                                />
                                            </div>
                                            <p className="font-bold text-primary_one fs-lg mb-2">Deploy All Assets and Code</p>
                                            <p className="fs-md mb-0">
                                                We handle the final deployment and ensure everything is configured for live use
                                                without disruption.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Tabs.Item>
                            <Tabs.Item title="Aftercare">
                                <div className="lg:col-span-10 py-6 px-2 lg:px-9 md-px-2">
                                    <p className="font-semibold text-primary_one mb-7">
                                        Our job doesn&apos;t end at deployment. We stick around to support, scale, and optimize
                                        your system.
                                    </p>
                                    <div className="flex lg:flex-nowrap md:flex-wrap flex-wrap gap-7 lg:gap-0 md:gap-0">
                                        <div className="pr-0 lg:pr-7 md:pr-3">
                                            <div className="voice-generator-box mb-2">
                                                <Image
                                                    src="/img/moweb-supply-chain-icons/ic-knowledge-transfer.svg"
                                                    width={36}
                                                    height={36}
                                                    alt="ai_voice_generator"
                                                />
                                            </div>
                                            <p className="font-bold text-primary_one fs-lg mb-2">
                                                Knowledge Transfer to Client Team
                                            </p>
                                            <p className="fs-md mb-0">
                                                We train your internal teams to manage the system confidently and efficiently.
                                            </p>
                                        </div>
                                        <div className="border-r-0 border-l-0 lg:border-r lg:border-l md:border-r md:border-l pl-0 lg:pl-7 md:pl-3 pr-0 lg:pr-7 md:pr-3">
                                            <div className="voice-generator-box mb-2">
                                                <Image
                                                    src="/img/moweb-supply-chain-icons/ic-contract-based.svg"
                                                    width={36}
                                                    height={36}
                                                    alt="ai_voice_generator"
                                                />
                                            </div>
                                            <p className="font-bold text-primary_one fs-lg mb-2">Contract-Based Support</p>
                                            <p className="fs-md mb-0">
                                                Choose ongoing support packages for updates, issue resolution, and technical
                                                enhancements.
                                            </p>
                                        </div>
                                        <div className="pl-0 lg:pl-7 md:pl-3">
                                            <div className="voice-generator-box mb-2">
                                                <Image
                                                    src="/img/moweb-supply-chain-icons/ic-release-notes.svg"
                                                    width={36}
                                                    height={36}
                                                    alt="ai_voice_generator"
                                                />
                                            </div>
                                            <p className="font-bold text-primary_one fs-lg mb-2">Release Notes / User Manuals</p>
                                            <p className="fs-md mb-0">
                                                If needed, we provide clear documentation for users, admins, and stakeholders to
                                                stay aligned.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Tabs.Item>
                        </Tabs.Group>
                    </div>
                </div>
                <div className="row grid lg:grid-cols-12">
                    <div className="lg:col-span-12">
                        <h2 className="text-white mb-3">Reimagine Your Supply Chain with the Power of AI</h2>
                        <p className="text-white mb-3">
                            Automate operations, predict demand, and make data-driven decisions at every stage. Future-proof your
                            logistics with AI-driven supply chain software tailored to your business.
                        </p>
                        <div>
                            <Link
                                href="/contact-us"
                                className="btn btn-white border-white bg-white dark:bg-black text-blue hover:bg-blue hover:text-white inline-block "
                            >
                                {'Schedule Your Free AI Strategy Session'}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default ScmSoftwareDevelopment;
